import { createStart, createMiddleware } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";

import { renderErrorPage } from "./lib/error-page";
import { attachSupabaseAuth } from "@/lib/attach-supabase-auth-lazy";
import { detectBot } from "@/lib/bot-detect";
import { getRequestHost } from "@/lib/request-host";
import { stripEnPrefix, enDuplicateTarget } from "@/lib/en-host";
import { isEnDomainPath } from "@/lib/locale-paths";
import { englishContentRedirect, verifiedTrArticleRedirect, legacyPathRedirect, isGoneLegacyPath } from "@/lib/redirects";
import { goneResponse } from "@/lib/gone.server";

const errorMiddleware = createMiddleware().server(async ({ next, request }) => {
  const url = new URL(request.url);
  if (url.pathname.startsWith("/lovable/")) {
    return next();
  }
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: {
        "content-type": "text/html; charset=utf-8",
        ...SECURITY_HEADERS,
      },
    });
  }
});

// HTTP security headers applied to every server response.
// - CSP: restricts allowed sources; allows Google Fonts, inline styles (Tailwind),
//   the WP REST gateway and Lovable analytics endpoints used by the site.
// - HSTS: enforce HTTPS for 2 years incl. subdomains.
// - X-Frame-Options + frame-ancestors: clickjacking protection.
// - X-Content-Type-Options: block MIME sniffing.
// - Referrer-Policy: minimal referrer leakage.
// - Permissions-Policy: disable powerful APIs we don't use.
const SECURITY_HEADERS: Record<string, string> = {
  "Strict-Transport-Security": "max-age=63072000; includeSubDomains; preload",
  "X-Content-Type-Options": "nosniff",
  // X-Frame-Options intentionally omitted: legacy header that cannot allow-list
  // multiple origins. Clickjacking protection is enforced via CSP frame-ancestors
  // below, which allows the Lovable editor preview iframe.
  "Referrer-Policy": "strict-origin-when-cross-origin",
  "Permissions-Policy":
    "camera=(), microphone=(), geolocation=(), payment=(), usb=(), magnetometer=(), gyroscope=(), accelerometer=()",
  "Content-Security-Policy": [
    "default-src 'self'",
    "base-uri 'self'",
    "object-src 'none'",
    "frame-ancestors 'self' https://*.lovable.app https://*.lovableproject.com https://lovable.dev https://*.lovable.dev",
    "frame-src 'self' https://www.youtube-nocookie.com https://www.youtube.com",
    "form-action 'self'",
    "img-src 'self' data: blob: https:",
    "font-src 'self' data: https://fonts.gstatic.com",
    "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
    "script-src 'self' 'unsafe-inline' 'unsafe-eval' https://www.googletagmanager.com https://www.google-analytics.com https://connect.facebook.net",
    "connect-src 'self' https: wss:",
    "media-src 'self' https: data:",
    "upgrade-insecure-requests",
  ].join("; "),
};

// Paths that must never be cached at the edge — they render per-user state,
// return Set-Cookie, or handle auth/writes.
const NO_CACHE_PREFIXES = [
  "/admin",
  "/api/",
  "/lovable/",
  "/email",
  "/hesabim",
  "/login",
  "/mcp",
];

function isEdgeCacheable(pathname: string, method: string): boolean {
  if (method !== "GET" && method !== "HEAD") return false;
  for (const p of NO_CACHE_PREFIXES) {
    if (pathname === p || pathname.startsWith(p)) return false;
  }
  return true;
}

const securityHeadersMiddleware = createMiddleware().server(async ({ next, request }) => {
  const url = new URL(request.url);
  if (url.pathname.startsWith("/lovable/")) {
    return next();
  }
  const result = await next();
  // result may be a Response object or a TanStack result envelope.
  const maybeResponse: unknown = (result as { response?: unknown })?.response ?? result;
  if (maybeResponse instanceof Response) {
    for (const [key, value] of Object.entries(SECURITY_HEADERS)) {
      if (!maybeResponse.headers.has(key)) {
        maybeResponse.headers.set(key, value);
      }
    }

    // Edge-cache public GETs. Browser revalidates every request (max-age=0)
    // so users get fresh HTML on hard reload, while Cloudflare serves the
    // cached copy to everyone else for an hour + SWR for a day. Skips
    // routes that carry auth cookies or per-user state.
    try {
      const hasCache = maybeResponse.headers.has("cache-control");
      const hasSetCookie = maybeResponse.headers.has("set-cookie");
      const ct = maybeResponse.headers.get("content-type") ?? "";
      const status = maybeResponse.status;
      if (
        !hasCache &&
        !hasSetCookie &&
        status >= 200 &&
        status < 400 &&
        isEdgeCacheable(url.pathname, request.method) &&
        ct.includes("text/html")
      ) {
        maybeResponse.headers.set(
          "cache-control",
          "public, max-age=0, must-revalidate",
        );
        // Cloudflare-specific: honor s-maxage at the edge regardless of
        // browser cache-control. 1h fresh, 1d stale-while-revalidate.
        maybeResponse.headers.set(
          "cdn-cache-control",
          "public, s-maxage=60, stale-while-revalidate=300",
        );
      }
    } catch {
      // never break the response over cache header logic
    }
  }
  return result;
});


// Logs bot GETs to HTML page routes into page_views. Bots don't run JS, so the
// client beacon never fires for them; this is the only way to see which AI
// crawlers actually fetch content. Filters out assets, APIs, admin, and any
// path with a file extension to keep write volume small.
const botLoggerMiddleware = createMiddleware().server(async ({ next, request }) => {
  const result = await next();
  try {
    if (request.method !== "GET") return result;
    const url = new URL(request.url);
    const p = url.pathname;
    if (
      p.startsWith("/api/") ||
      p.startsWith("/admin") ||
      p.startsWith("/lovable/") ||
      p.startsWith("/_") ||
      p.startsWith("/assets/") ||
      /\.[a-zA-Z0-9]{2,5}$/.test(p) // has file extension
    ) {
      return result;
    }
    const ua = request.headers.get("user-agent");
    const botName = detectBot(ua);
    if (!botName) return result;
    const host = getRequestHost(request, url);

    const supabaseUrl = process.env.SUPABASE_URL;
    const serviceKey = process.env.SUPABASE_SERVICE_ROLE_KEY;
    if (!supabaseUrl || !serviceKey) return result;

    const supabase = createClient(supabaseUrl, serviceKey, {
      auth: { persistSession: false, autoRefreshToken: false },
    });
    // Fire-and-forget: never let logging failures affect the response.
    void supabase
      .from("page_views")
      .insert({
        path: p.slice(0, 500),
        session_id: `bot_${botName}`.slice(0, 64).replace(/[^a-zA-Z0-9_-]/g, "_"),
        host,
        user_agent: (ua ?? "").slice(0, 500),
        referrer: request.headers.get("referer")?.slice(0, 2000) ?? null,
        bot_name: botName,
        ai_source: null,
        is_bot: true,
      })
      .then(() => {}, () => {});
  } catch {
    // never affect response
  }
  return result;
});

// This project serves Turkish only; retired English prefixes never redirect across sites.
const REDIRECT_HEADERS = { "cache-control": "public, max-age=3600" };

/** Static Turkish guide pages that live under /yazilar/ outside the posts table. */
const STATIC_ESSAY_SLUGS = new Set([
  "surdurulebilirlik-nedir-rehberi",
  "greenwashing-nedir",
  "sdg-rehberi",
  "yesil-mutabakat-rehberi",
  "surdurulebilir-kalkinma-nedir",
]);

/** Returns /yazilar/{slug} only if that exact essay is published; null otherwise. */
async function existingEssayPath(slug: string): Promise<string | null> {
  if (!/^[a-z0-9-]{1,200}$/.test(slug)) return null;
  if (STATIC_ESSAY_SLUGS.has(slug)) return `/yazilar/${slug}`;
  const supabaseUrl = process.env.SUPABASE_URL;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY ?? import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  if (!supabaseUrl || !key) return null;
  try {
    const supabase = createClient(supabaseUrl, key, { auth: { persistSession: false, autoRefreshToken: false } });
    const { data } = await supabase.from("posts").select("slug").eq("published", true).eq("slug", slug).maybeSingle();
    return data ? `/yazilar/${slug}` : null;
  } catch {
    return null;
  }
}

/** Resolve exact legacy decisions before routing or slash normalization. */
const legacyRedirectMiddleware = createMiddleware().server(async ({ next, request }) => {
  const url = new URL(request.url);
  const path = url.pathname;
  const clean = path.length > 1 ? path.replace(/\/+$/, "") || "/" : path;
  const alias = legacyPathRedirect(clean);
  if (alias) return new Response(null, { status: 301, headers: { location: `${alias.to_url}${url.search}`, ...REDIRECT_HEADERS } });
  const article = verifiedTrArticleRedirect(clean);
  if (article) return new Response(null, { status: 301, headers: { location: `${article}${url.search}`, ...REDIRECT_HEADERS } });
  if (isGoneLegacyPath(clean)) return goneResponse(request.method);
  const essay = /^\/essays\/([^/]+)$/.exec(clean);
  if (essay) {
    const target = await existingEssayPath(decodeURIComponent(essay[1]));
    if (target) return new Response(null, { status: 301, headers: { location: `${target}${url.search}`, ...REDIRECT_HEADERS } });
    return new Response("Not found", { status: 404, headers: { "content-type": "text/plain; charset=utf-8" } });
  }
  if (isEnDomainPath(clean) || enDuplicateTarget(clean) || englishContentRedirect(stripEnPrefix(clean))) {
    return new Response("Not found", { status: 404, headers: { "content-type": "text/plain; charset=utf-8" } });
  }
  if (clean !== path && !path.startsWith("/api/")) return new Response(null, { status: 308, headers: { location: `${clean}${url.search}`, ...REDIRECT_HEADERS } });
  return next();
});


export const startInstance = createStart(() => ({
  requestMiddleware: [
    securityHeadersMiddleware,
    legacyRedirectMiddleware,
    botLoggerMiddleware,
    errorMiddleware,
  ],
  functionMiddleware: [attachSupabaseAuth],
}));

